import { useState } from 'react';
import { format } from 'date-fns';
import { ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  ADMIN_FLAG_REASON_MAX, ADMIN_FLAG_REASON_MIN, canAdminFlag, flagVideo, type AdminFlagFields,
} from '@/lib/admin-video-flag';

type Video = AdminFlagFields & {
  id: string;
  tiktok_video_url: string | null;
  review_status: string | null;
  deleted: boolean | null;
};

/** Per-hire tally for the Creator Payments listing, next to HireFlagCountBadge
 *  (the brand's own flag count) — a separate signal, not a substitute for it. */
export function AdminFlagCountBadge({ count }: { count: number }) {
  if (count === 0) return null;
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex cursor-default items-center gap-1 rounded-full border border-orange-200 bg-orange-100 px-2 py-0.5 text-xs font-semibold text-orange-800">
            <ShieldAlert className="h-3 w-3 shrink-0" />
            {count} admin flagged
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[280px]">
          <p className="font-semibold">A BMC admin flagged {count} video{count === 1 ? '' : 's'}</p>
          <p className="mt-0.5 text-xs leading-snug opacity-90">
            Held from auto-approve and auto-refund. Open the hire to see why and review it by hand.
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function AdminVideoFlagCell({ video, hireId, hire, onChanged }: {
  video: Video;
  hireId: string;
  hire: { payment_status: string | null; settled_at: string | null } | null | undefined;
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const ctx = { hireId, tiktokVideoUrl: video.tiktok_video_url };
  const trimmed = reason.trim();
  const valid = trimmed.length >= ADMIN_FLAG_REASON_MIN && trimmed.length <= ADMIN_FLAG_REASON_MAX;

  const submitFlag = async () => {
    setBusy(true);
    try {
      await flagVideo(video.id, trimmed, ctx);
      toast.success('Video flagged — it will not be auto-approved or auto-refunded');
      setOpen(false);
      setReason('');
      onChanged();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (video.admin_flagged_at) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border border-orange-200 bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-800 cursor-default">
              <ShieldAlert className="h-3 w-3 shrink-0" /> Flagged
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs text-xs space-y-1">
            <p className="whitespace-pre-wrap">{video.admin_flag_reason}</p>
            <p className="text-muted-foreground">
              {video.admin_flagged_by_email ?? 'Admin'} · {format(new Date(video.admin_flagged_at), 'MMM d, yyyy')}
            </p>
            <p className="text-muted-foreground">Auto-approve and auto-refund are held.</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  if (!canAdminFlag(video, hire)) return <span className="text-xs text-muted-foreground">—</span>;

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        className="h-6 shrink-0 whitespace-nowrap px-2 text-xs text-orange-700 border-orange-200 hover:bg-orange-50"
        onClick={() => setOpen(true)}
      >
        Flag
      </Button>
      <Dialog open={open} onOpenChange={o => { if (!busy) setOpen(o); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Flag this video</DialogTitle>
            <DialogDescription>
              A flagged video is never auto-approved, and its hire is never auto-refunded. The flag
              can&apos;t be removed. Approving or rejecting it by hand (admin or brand) still works.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="Why is this video on hold? (internal note)"
            maxLength={ADMIN_FLAG_REASON_MAX}
            rows={4}
          />
          <DialogFooter>
            <Button variant="outline" disabled={busy} onClick={() => setOpen(false)}>Cancel</Button>
            <Button disabled={busy || !valid} onClick={submitFlag}>
              {busy ? 'Flagging…' : 'Flag video'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
